import React from 'react'
import PropTypes from 'prop-types';

import {ValidationResult, revalidate, validate, required, limitedLength, emailFormat, allAreEqual, limitedFileSize, limitedFileType, format, switchRules, focusFirstError} from 'validation'
import BaseComponent from 'common/BaseComponent'
import {getMessage as msg} from 'MessageBundle'
import {replacePlaceholders} from 'common/Replace'
import update from 'immutability-helper';
import theme from 'dialogs/Complaint.scss'
import conf from 'configuration.json'
import Information from './Information'
import FileUploader from 'common/FileUploader'
import Input from 'common/Input'
import TextArea from 'common/TextArea'
import Select from 'common/Select'
import ErrorBox from 'common/ErrorBox'
import ProgressBar from 'common/ProgressBar'

const MAX_FILE_SIZE = 5*1024*1024;
const ANONYMOUS_ALLOWED_TYPES = ["application/pdf", "image/jpg", "image/jpeg", "image/png", "image/tiff"];
const ALLOWED_TYPES = ["application/msword", "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
    "application/vnd.ms-excel", "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"].concat(ANONYMOUS_ALLOWED_TYPES);

export default class Form extends BaseComponent {

    constructor(props) {
        super(props);

        this.allowedTypes = props.authenticated ? ALLOWED_TYPES : ANONYMOUS_ALLOWED_TYPES;
        this.validation = this.getValidation(this.allowedTypes);
        this.elementOrder = {
            vo : "vo",
            email : "email",
            emailConfirmation : "emailConfirmation",
            reason : "reason",
            invoice : "invoice",
            creditNote : "creditNote",
            deliveryNote : "deliveryNote",
            newImei : "newImei",
            imei : "newImei",
            debitNote : "debitNote",
            comment : "comment",
            attachments : "fileUploader"
        };
        this.state = this.getInitialState();
        this.state.processing = false;
        if (props.vo !== undefined) {
            // VPQ Operators have dummy VO that should not be filled
            this.state.vo = !props.vpqOperator ? props.vo : "";
        }
        if (props.email !== undefined) {
            this.state.email = props.email;
        }
    }

    getInitialState = () => {
        return {
            infoType: "",
            newImei: "",
            vo: this.props.authenticated && !this.props.vpqOperator ? this.props.vo : "",
            email: "",
            emailConfirmation: "",
            reason: "",
            invoice: "",
            creditNote: "",
            deliveryNote: "",
            imei: [],
            amount: "",
            debitNote: "",
            externalReference: "",
            comment: "",
            attachments: [],
            errorMessages: {}
        };
    };

    getValidation(allowedFileTypes) {
        return {
            main: [
                {
                    properties: ["state.vo"],
                    individualNames: ["vo"],
                    strategies: [
                        required(msg("newComplaintValidation.voFieldEmpty")),
                        limitedLength(5, 5, msg("newComplaintValidation.voFormat"))
                    ]
                }, {
                    properties: ["state.email"],
                    individualNames: ["email"],
                    strategies: [
                        required(msg("newComplaintValidation.emailFieldEmpty")),
                        emailFormat(msg("newComplaintValidation.emailFormat"))
                    ]
                }, {
                    properties: ["state.emailConfirmation"],
                    individualNames: ["emailConfirmation"],
                    strategies: [
                        required(msg("newComplaintValidation.emailConfirmFieldEmpty"))
                    ]
                }, {
                    properties: ["state.email", "state.emailConfirmation"],
                    individualNames: ["email", "emailConfirmation"],
                    strategies: [
                        allAreEqual(msg("newComplaintValidation.emailsNotEqual"))
                    ]
                }, {
                    properties: ["state.reason"],
                    individualNames: ["reason"],
                    strategies: [
                        required(msg("newComplaintValidation.complaintReasonEmpty"))
                    ]
                }, {
                    properties: ["state.debitNote"],
                    individualNames: ["debitNote"],
                    strategies: [
                        limitedLength(0, 20, msg("newComplaintValidation.debitNoteFormat"))
                    ]
                }, {
                    properties: ["state.comment"],
                    individualNames: ["comment"],
                    strategies: [
                        required(msg("newComplaintValidation.commentEmpty"))
                    ]
                }, {
                    properties: ["state.attachments"],
                    individualNames: ["attachments"],
                    strategies: [
                        limitedFileSize(MAX_FILE_SIZE, msg('newComplaintValidation.fileSizeError')),
                        limitedFileType(allowedFileTypes, msg('newComplaintValidation.fileTypeError'))
                    ]
                }, {
                    properties: ["state"],
                    strategies: [
                        switchRules("infoType", {
                            invoice: [{
                                properties: ["invoice"],
                                individualNames: ["invoice"],
                                strategies: [
                                    required(msg("newComplaintValidation.invoiceEmpty")),
                                    limitedLength(10, 10, msg("newComplaintValidation.invoiceWrongFormat")),
                                    format(/^\d+$/, msg("newComplaintValidation.invoiceWrongFormat"))
                                ]
                            }],
                            creditNote: [{
                                properties: ["creditNote"],
                                individualNames: ["creditNote"],
                                strategies: [
                                    required(msg("newComplaintValidation.creditNoteEmpty")),
                                    limitedLength(10, 10, msg("newComplaintValidation.creditNoteFormat")),
                                    format(/^\d+$/, msg("newComplaintValidation.creditNoteFormat"))
                                ]
                            }],
                            deliveryNote: [{
                                properties: ["deliveryNote"],
                                individualNames: ["deliveryNote"],
                                strategies: [
                                    required(msg("newComplaintValidation.deliveryEmpty")),
                                    limitedLength(10, 10, msg("newComplaintValidation.deliveryNoteNumberFormat")),
                                    format(/^\d+$/, msg("newComplaintValidation.deliveryNoteNumberFormat"))
                                ]
                            }],
                            imei: [{
                                properties: ["imei"],
                                individualNames: ["imei"],
                                strategies: [
                                    required(msg("newComplaintValidation.imeiEmpty"))
                                ]
                            }]
                        })
                    ]
                }
            ],
            imeiAddition : [
                {
                    properties: ["state.newImei"],
                    individualNames: ["newImei"],
                    strategies: [
                        required(msg("newComplaintValidation.imeiEmpty")),
                        limitedLength(15, 15, msg("newComplaintValidation.imeiFormat")),
                        format(/^\d+$/, msg("newComplaintValidation.imeiFormat"))
                    ]
                }
            ]
        };
    }

    handleInfoTypeChange = (infoType) => {
        this.setState({
            infoType: infoType,
            invoice: "",
            creditNote: "",
            deliveryNote: "",
            imei: [],
            newImei: ""
        });
    };

    handleReset = () => {
        this.setState(this.getInitialState());
    };

    handleImeiAddition = () => {
        const validationResult = revalidate(this, this.validation, ["imeiAddition"], this.state.errorMessages);
        if (validationResult.isEmpty(["imeiAddition"])) {
            this.setState({
                imei: update(this.state.imei, {$push: [this.state.newImei]}),
                newImei: ""
            });
        } else {
            let newErrorMessages = validationResult.getErrorMessages();
            this.setState({
                errorMessages: newErrorMessages
            });
            focusFirstError(this.elementOrder, newErrorMessages);
        }
    };

    handleImeiRemoval = (index) => () => {
        this.setState({
            imei: update(this.state.imei, {$splice: [[index, 1]]})
        });
    };

    handleSubmit = () => {
        const validationResult = validate(this, this.validation, ["main"]);
        if (validationResult.isEmpty()) {
            this.setState({
                processing: true,
                errorMessages: {}
            });
            let complaintDetails = JSON.stringify({
                vo: this.state.vo,
                email: this.state.email,
                reason: this.state.reason,
                invoice: this.state.invoice,
                creditNote: this.state.creditNote,
                deliveryNote: this.state.deliveryNote,
                imei: this.state.imei,
                amount: this.state.amount,
                debitNote: this.state.debitNote,
                externalReference: this.state.externalReference,
                comment: this.state.comment
            });
            let formData = new FormData();
            formData.append("complaintDetails", new Blob([complaintDetails], {type: "application/json"}));
            this.uploadAttachments(this.state.attachments, formData, "attachments", this.createComplaint);
        } else {
            let newErrorMessages = validationResult.getErrorMessages();
            this.setState({
                errorMessages: newErrorMessages
            });
            focusFirstError(this.elementOrder, newErrorMessages);
        }
    };

    getProcessingErrors = () => {
        return new Map([
            ["WRONG_VO", {
                message: replacePlaceholders(msg("newComplaintValidation.unknownVo"), this.state.vo),
                invalidComponent: "vo"
            }],
            ["INVALID_ATTACHMENT_CONTENT", {
                message: msg("common.attachmentValidationFault"),
                invalidComponent: "attachments"
            }],
            ["INVALID_ATTACHMENT_SIZE", {
                message: msg("newComplaintValidation.fileSizeError"),
                invalidComponent: "attachments"
            }]
        ]);
    };

    createComplaint = (formData) => {
        this.request('post', conf.rest.complaintEndpoint).params(formData).send((result) => {
            this.props.onComplete(result.body);
        }, (error, result) => {
            let errorText;
            let invalidComponent;
            if (result && result.statusCode === 412) {
                const errorDescriptor = this.getProcessingErrors().get(result.body.message);
                errorText = errorDescriptor.message;
                invalidComponent = errorDescriptor.invalidComponent;
            } else if (result && result.status === 403) {
                errorText = msg("newComplaintValidation.intrusionLocked");
                invalidComponent = [];
            } else {
                errorText = msg("common.technicalError");
                invalidComponent = [];
            }
            const validationResult = new ValidationResult(this.state.errorMessages);
            validationResult.setGroup("manual", {[invalidComponent]: [errorText]});

            let newErrorMessages = validationResult.getErrorMessages();
            this.setState({
                errorMessages: newErrorMessages,
                processing: false
            });
            focusFirstError(this.elementOrder, newErrorMessages);
        });
    };

    render() {
        return (
            <div className={theme.container}>
                <h2>{msg('newComplaint.header')}</h2>
                <div className={theme.clear}>
                    <ErrorBox groups={["main", "imeiAddition", "manual"]} errorMessages={this.state.errorMessages}/>
                    <div className={theme.rowId}>
                        <Input id="vo" value={this.state.vo} name="vo" errorMessages={this.state.errorMessages}
                            onChange={this.handleChange("vo")} required label={msg("newComplaint.vo")}
                            disabled={this.props.authenticated && !this.props.vpqOperator}/>
                    </div>
                    <div className={theme.coll + " " + theme.heightFixed}>
                        <div className={theme.row}>
                            <Input id="email" value={this.state.email} name="email" errorMessages={this.state.errorMessages}
                                onChange={this.handleChange("email")} required label={msg("newComplaint.email") } copyDenied/>
                        </div>
                        <div className={theme.row}>
                            <Input id="emailConfirmation" value={this.state.emailConfirmation} name="emailConfirmation" errorMessages={this.state.errorMessages}
                                onChange={this.handleChange("emailConfirmation")} required label={msg("newComplaint.emailConfirmation")} copyDenied/>
                        </div>
                        <div className={theme.rowReasonComplaint}>
                            <Select id="reason" value={this.state.reason} name="reason" errorMessages={this.state.errorMessages}
                                options={new Map([...this.props.complaintReasons.entries()].sort((a, b) => {
                                    return a[1].localeCompare(b[1]);
                                }))} onChange={this.handleChange("reason")} required label={msg("newComplaint.reason")} />
                        </div>
                        <Information invoice={this.state.invoice} creditNote={this.state.creditNote} deliveryNote={this.state.deliveryNote} imei={this.state.imei}
                            infoType={this.state.infoType} onTypeChange={this.handleInfoTypeChange} onChange={this.handleChange} errorMessages={this.state.errorMessages}
                            newImei={this.state.newImei} onImeiAddition={this.handleImeiAddition} onImeiRemoval={this.handleImeiRemoval}/>
                    </div>
                    <div className={theme.colr}>
                        <div className={theme.row}>
                            <Input id="amount" value={this.state.amount} onChange={this.handleChange("amount")} label={msg("newComplaint.amount")}
                                allowedSymbols={"^[0-9.,]$"}/>
                        </div>
                        <div className={theme.debitNote}>
                            <Input id="debitNote" value={this.state.debitNote} name="debitNote" errorMessages={this.state.errorMessages}
                                onChange={this.handleChange("debitNote")} label={msg("newComplaint.debitNote")} tooltip={msg("newComplaint.debitNote.toolTip")}/>
                        </div>
                        <div className={theme.row + ' ' + theme.rowDecor}>
                            <Input id="externalReference" value={this.state.externalReference} onChange={this.handleChange("externalReference")}
                                label={msg("newComplaint.externalReference")} tooltip={msg("newComplaint.externalReference.toolTip")}/>
                        </div>
                        <div className={theme.rowComment}>
                            <TextArea id="comment" value={this.state.comment} name="comment" errorMessages={this.state.errorMessages} max={4000}
                                onChange={this.handleChange("comment")} label={msg("newComplaint.comment")} required tooltip={msg("newComplaint.commentTooltip")}/>
                        </div>
                        <FileUploader id="fileUploader" value={this.state.attachments} className={theme.rowFileUpload + " " + theme.rowDecor}
                            name="attachments" errorMessages={this.state.errorMessages} authenticated={this.props.authenticated}
                            onChange={this.handleChange('attachments')} allowedTypes={this.allowedTypes} />
                    </div>
                </div>
                <div className={theme.btnGroup + " " + theme.btnGroupDecor}>
                    <span><span>*</span>{msg('newComplaint.mandatoryInfo')}</span>
                    <button id="reset-btn" onClick={this.handleReset}>{msg('newComplaint.resetBtn')}</button>
                    <button id="cancel-btn" onClick={this.closeWindow}>{msg('newComplaint.cancelBtn')}</button>
                    <button id="submit-btn" className={theme.accent} onClick={this.handleSubmit}>{msg('newComplaint.submitBtn')}</button>
                </div>
                <ProgressBar hidden={!this.state.processing} />
            </div>
        )
    }
}

Form.propTypes = {

    /**
     * VO of the current user (undefined the user is anonymous).
     */
    vo: PropTypes.string,

    /**
     * Email of the current user (undefined the user is anonymous).
     */
    email: PropTypes.string,

    /**
     * True if the current user is authenticated.
     */
    authenticated: PropTypes.bool.isRequired,

    /**
     * True if the current user is a VPQ Operator.
     */
    vpqOperator: PropTypes.bool.isRequired,

    /**
     * Map of the available complaint reasons.
     */
    complaintReasons: PropTypes.object.isRequired,

    /**
     * JavaScript function that has to be executed on form submitting.
     */
    onComplete: PropTypes.func.isRequired,
};


// WEBPACK FOOTER //
// ./src/dialogs/NewComplaint/Form/index.js